import { Flex, useMantineTheme } from '@mantine/core';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useSearchParams } from 'react-router-dom';
import { useDeal } from '@dealstack/frontend/shared/modules/deal/hooks/useDeal';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import { StepTabs } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/StepTabs';
import { useKycSteps } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/hook/useKycSteps';
import { useFileSelection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/hook/useFileSelection';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Overview } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/Overview/Overview';
import { FormsCategoryList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/FormsCategoryList';
import { PivCardList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/PivCardList/PivCardList';
import { NavigationFooter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/NavigationFooter/NavigationFooter';
import { StepContent } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/StepContent';
import { getFullName } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/utils/getFullName';
import { useInvestor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/hook/useInvestor';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { getInvestorKycFiles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/utils/getInvestorKycFiles';
import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';
import { getDeclarationsApprovalStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/utils/getDeclarationsApprovalStatus';
import { getKycStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/utils/getKycStatus';
import { getKycStatusByFileType } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/utils/getKycStatusByFileType';
import { overallFormStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/utils/overallFormStatus';
import { useFormStepPillFiles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepTabs/useFormStepPillFiles';
import { KYCFileType, KYCWorkflowStatus } from '@dealstack/shared/lib/generated';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { useMemo } from 'react';
import { useKycBreadcrumbs } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/Header/hook/useKycBreadcrumbs.tsx';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';

export const ReviewKycWorkflow = () => {
	const theme = useMantineTheme();
	const tMipManage = useTranslationMipManage();
	const { mipId, dealId } = useRequiredParams('mipId', 'dealId', 'investorId');
	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;
	const { data: deal } = useDeal({ dealId });
	const { currentInvestor, paginatedInvestors: getPaginatedInvestors, isLoading } = useInvestor();

	const { data: workflowKycSetting, isLoading: isKycSettingLoading } = useKycWorkflowKycSetting(
		kycWorkflowId ?? ''
	);

	const investorKycFiles = getInvestorKycFiles(currentInvestor);
	const formStepPillFiles = useFormStepPillFiles({ mipId, currentInvestor });
	const hasKycForms = formStepPillFiles.length > 0;
	const isReadOnly =
		currentInvestor?.isInvestmentCompleted ||
		currentInvestor?.status === KYCWorkflowStatus.WITHDRAWN;

	const isDataReady = !isLoading && !isKycSettingLoading;

	const isProofOfIdentityRequired = workflowKycSetting?.isProofOfIdentityRequired ?? true;
	const isProofOfAddressRequired = workflowKycSetting?.isProofOfAddressRequired ?? true;
	const isProofOfInvestmentVehicleRequired =
		workflowKycSetting?.isProofOfInvestmentVehicleRequired ?? true;
	const anyDeclarationRequired =
		(workflowKycSetting?.isBeneficialOwnershipRequired ?? true) ||
		(workflowKycSetting?.isSourceOfFundsRequired ?? true) ||
		(workflowKycSetting?.isPepRequired ?? true);

	const configuredSteps: Step[] = [
		{
			type: StepType.DECLARATIONS,
			title: tMipManage('kyc.page.declarations.title'),
			description: tMipManage('kyc.page.declarations.description'),
			approvalStatus: getDeclarationsApprovalStatus(currentInvestor),
			isSelected: true,
			isDisplayed: anyDeclarationRequired,
		},
		{
			type: StepType.PROOF_OF_IDENTITY,
			title: tMipManage('kyc.page.proof_of_identity.title'),
			description: tMipManage('kyc.page.proof_of_identity.description'),
			approvalStatus: getKycStatus(investorKycFiles?.PROOF_OF_IDENTITY?.[0]),
			isSelected: false,
			isDisplayed: isProofOfIdentityRequired,
			allowFileUpload: true,
			allowMultipleFileUpload: false,
		},
		{
			type: StepType.PROOF_OF_ADDRESS,
			title: tMipManage('kyc.page.proof_of_address.title'),
			description: tMipManage('kyc.page.proof_of_address.description'),
			approvalStatus: getKycStatus(investorKycFiles?.PROOF_OF_ADDRESS?.[0]),
			isSelected: false,
			isDisplayed: isProofOfAddressRequired,
			allowFileUpload: true,
			allowMultipleFileUpload: false,
		},
		{
			type: StepType.INVESTMENT_VEHICLE,
			title: tMipManage('kyc.page.proof_of_investment_vehicle.title'),
			description: tMipManage('kyc.page.proof_of_investment_vehicle.description'),
			approvalStatus: getKycStatusByFileType({
				currentInvestor,
				investorKycFiles,
				type: KYCFileType.INVESTMENT_VEHICLE,
			}),
			isSelected: false,
			isDisplayed: !!currentInvestor?.piv && isProofOfInvestmentVehicleRequired,
			allowFileUpload: true,
			allowMultipleFileUpload: true,
		},
		{
			type: StepType.FORM,
			title: tMipManage('kyc.modal.forms'),
			description: tMipManage('kyc.page.form.title'),
			approvalStatus: overallFormStatus(formStepPillFiles),
			isSelected: false,
			isDisplayed: hasKycForms,
			allowFileUpload: hasKycForms,
			allowMultipleFileUpload: true,
		},
	].filter((step) => step.isDisplayed);

	const {
		currentStep,
		moveToStep,
		moveToNextStep,
		moveToPreviousStep,
		canMoveToNextStep,
		canMoveToPreviousStep,
	} = useKycSteps({
		steps: configuredSteps,
		initialStepIndex: 0,
	});

	const {
		setSelectedKycFileId,
		filesForStep,
		selectedKycFile,
		selectedKycFileWithAutoSelection,
		selectedTemplate,
		setSelectedTemplateId,
	} = useFileSelection({ currentInvestor, currentStep, mipId });

	const isFormStep = currentStep?.type === StepType.FORM;
	const isPivStep = currentStep?.type === StepType.INVESTMENT_VEHICLE;

	// In the FORM step the preview defaults to the selected form; clicking an uncategorized (OTHER) file
	// selects it instead. Restrict to OTHER so a file id carried over from a proof step never leaks in.
	const resolvePreviewKycFile = () => {
		if (!isFormStep) {
			return selectedKycFileWithAutoSelection;
		}
		return selectedKycFile?.type === KYCFileType.OTHER ? selectedKycFile : undefined;
	};

	const previewKycFile = resolvePreviewKycFile();

	const currentMip = useMemo(() => deal?.mips.find((mip) => mip.id === mipId), [deal, mipId]);

	useKycBreadcrumbs({
		view: 'home',
		dealId: deal?.id,
		currentMip,
	});

	return (
		<Flex h="100%" direction="column" gap="1rem" p="1rem 2rem" style={{ overflow: 'hidden' }}>
			<PageHeader
				pageTitle={getFullName(
					currentInvestor?.user.firstName,
					currentInvestor?.user.lastName
				)}
				hideBreadcrumbs
			/>

			{isDataReady && (
				<StepTabs
					currentStep={currentStep}
					steps={configuredSteps}
					moveToStep={moveToStep}
					currentInvestor={currentInvestor}
					mipId={mipId}
				/>
			)}

			<FlexExtended
				direction="column"
				border={`1px solid ${theme.colors.gray[4]}`}
				borderRadius="0.25rem"
				h="100%"
				miw="50rem"
				style={{ position: 'relative', overflow: 'auto' }}
			>
				{!isDataReady && <InlineLoader />}
				{isDataReady && (
					<>
						<FlexExtended direction="row" style={{ flex: 1, overflow: 'auto' }}>
							{isFormStep && (
								<FormsCategoryList
									mipId={mipId}
									currentStep={currentStep}
									currentInvestor={currentInvestor}
									selectedTemplate={selectedTemplate}
									onSelectForm={(card) => {
										setSelectedKycFileId(undefined);
										setSelectedTemplateId(card.templateId);
									}}
									selectedKycFileId={previewKycFile?.id}
									onSelectFile={(file) => setSelectedKycFileId(file.id)}
									isReadOnly={isReadOnly}
								/>
							)}
							{isPivStep && (
								<PivCardList
									currentStep={currentStep}
									currentInvestor={currentInvestor}
									selectedKycFileId={selectedKycFileWithAutoSelection?.id}
									onSelectFile={(file) => setSelectedKycFileId(file.id)}
									isReadOnly={isReadOnly}
								/>
							)}
							{!isFormStep && !isPivStep && (
								<Overview
									currentStep={currentStep}
									currentInvestor={currentInvestor}
									kycSetting={workflowKycSetting}
								/>
							)}
							<StepContent
								selectedKycFile={previewKycFile}
								currentStep={currentStep}
								currentInvestor={currentInvestor}
								filesForStep={filesForStep}
								isReadOnly={isReadOnly}
								selectedTemplate={selectedTemplate}
								kycSetting={workflowKycSetting}
							/>
						</FlexExtended>
						<NavigationFooter
							filesForStep={filesForStep}
							selectedKycFile={selectedKycFileWithAutoSelection}
							filteredInvestors={getPaginatedInvestors}
							currentInvestor={currentInvestor}
							moveToPreviousStep={moveToPreviousStep}
							moveToNextStep={moveToNextStep}
							moveToStep={moveToStep}
							canMoveToNextStep={canMoveToNextStep}
							canMoveToPreviousStep={canMoveToPreviousStep}
						/>
					</>
				)}
			</FlexExtended>
		</Flex>
	);
};
